import './App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AppProvider, useApp } from './context/AppContext';
import { ThemeProvider } from './context/ThemeContext';
import { PlayerProvider } from './context/PlayerContext';
import { Toaster } from './components/ui/sonner';

import Landing from './pages/Landing';
import Auth from './pages/Auth';
import AppLayout from './components/AppLayout';
import Home from './pages/Home';
import Watch from './pages/Watch';
import Channel from './pages/Channel';
import Studio from './pages/Studio';
import Upload from './pages/Upload';
import Library from './pages/Library';
import Search from './pages/Search';
import Category from './pages/Category';
import Memberships from './pages/Memberships';
import Admin from './pages/Admin';
import Owner from './pages/Owner';
import OwnerAds from './pages/OwnerAds';
import Ads from './pages/Ads';
import Shorts from './pages/Shorts';
import Marketing from './pages/Marketing';
import Earnings from './pages/Earnings';
import Legal from './pages/Legal';
import BillingResult from './pages/BillingResult';
import AdMarketplace from './pages/AdMarketplace';
import QrTool from './pages/QrTool';
import MediaLibraryPage from './pages/MediaLibrary';
import MigrateYouTube from './pages/MigrateYouTube';
import { HubBrowse, HubDetail, HubPublish, HubMy } from './pages/Hub';
import Changelog, { AdminChangelog } from './pages/Changelog';
import ListenerSignup from './pages/ListenerSignup';
import Tour from './pages/Tour';
import PwaInstallBanner from './components/PwaInstallBanner';
import ReaperCast from './pages/ReaperCast';
import Beta from './pages/Beta';
import Academy from './pages/Academy';
import AcademyActionCenter from './pages/AcademyActionCenter';
import Migrate from './pages/Migrate';
import Royalty from './pages/Royalty';
import PublicLayout from './components/PublicLayout';
import Income from './pages/Income';
import ForArtists from './pages/ForArtists';
import ForListeners from './pages/ForListeners';
import ForAgencies from './pages/ForAgencies';
import Research from './pages/Research';
import MusicianSignup from './pages/MusicianSignup';
import Reel from './pages/Reel';
import Artists from './pages/Artists';
import ListenerProfile from './pages/ListenerProfile';
import ForgotPassword from './pages/ForgotPassword';

function Protected({ children }) {
  const { user, booting } = useApp();
  if (booting) {
    return (
      <div className="min-h-screen bg-[#0a0a0c] flex items-center justify-center text-white/60 text-sm">Loading…</div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Auth mode="login" />} />
      <Route path="/signup" element={<Auth mode="signup" />} />
      <Route path="/signup/musician" element={<MusicianSignup />} />
      <Route path="/reapercast/signup" element={<MusicianSignup />} />
      <Route path="/signup/listener" element={<ListenerSignup />} />
      <Route path="/listener/signup" element={<ListenerSignup />} />
      <Route path="/tour" element={<Tour />} />
      <Route path="/changelog" element={<Changelog />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ForgotPassword />} />
      <Route path="/memberships" element={<Memberships />} />
      <Route path="/legal" element={<Legal />} />
      <Route path="/legal/:slug" element={<Legal />} />

      {/* Public marketing pages — wrapped in PublicLayout so navigation is everywhere. */}
      <Route element={<PublicLayout />}>
        <Route path="/for-artists" element={<ForArtists />} />
        <Route path="/for-listeners" element={<ForListeners />} />
        <Route path="/for-agencies" element={<ForAgencies />} />
      </Route>

      <Route element={<Protected><AppLayout /></Protected>}>
        <Route path="/home" element={<Home />} />
        <Route path="/shorts" element={<Shorts />} />
        <Route path="/watch/:id" element={<Watch />} />
        <Route path="/channel/:id" element={<Channel />} />
        <Route path="/studio" element={<Studio />} />
        <Route path="/upload" element={<Upload />} />
        <Route path="/library" element={<Library />} />
        <Route path="/search" element={<Search />} />
        <Route path="/category/:slug" element={<Category />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/owner" element={<Owner />} />
        <Route path="/owner/ads" element={<OwnerAds />} />
        <Route path="/ads" element={<Ads />} />
        <Route path="/ads/marketplace" element={<AdMarketplace />} />
        <Route path="/marketing" element={<Marketing />} />
        <Route path="/earnings" element={<Earnings />} />
        <Route path="/tools/qr" element={<QrTool />} />
        <Route path="/media" element={<MediaLibraryPage />} />
        <Route path="/migrate" element={<Migrate />} />
        <Route path="/migrate/youtube" element={<MigrateYouTube />} />
        <Route path="/reapercast" element={<ReaperCast />} />
        <Route path="/academy" element={<Academy />} />
        <Route path="/academy/action-center/:trackId" element={<AcademyActionCenter />} />
        <Route path="/income" element={<Income />} />
        <Route path="/royalty" element={<Royalty />} />
        <Route path="/beta" element={<Beta />} />
        <Route path="/research" element={<Research />} />
        <Route path="/feed" element={<Reel />} />
        <Route path="/artists" element={<Artists />} />
        <Route path="/listener" element={<ListenerProfile />} />
        <Route path="/me" element={<ListenerProfile />} />
        <Route path="/hub" element={<HubBrowse />} />
        <Route path="/hub/publish" element={<HubPublish />} />
        <Route path="/hub/publish/:id" element={<HubPublish />} />
        <Route path="/hub/my" element={<HubMy />} />
        <Route path="/hub/:id" element={<HubDetail />} />
        <Route path="/admin/changelog" element={<AdminChangelog />} />
        <Route path="/admin/changelog" element={<AdminChangelog />} />
        <Route path="/billing/success" element={<BillingResult kind="plan" />} />
        <Route path="/ads/success" element={<BillingResult kind="ad" />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <AppProvider>
          <PlayerProvider>
            <BrowserRouter>
              <AppRoutes />
              <PwaInstallBanner />
            </BrowserRouter>
            <Toaster position="top-center" richColors />
          </PlayerProvider>
        </AppProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
